import { useApi } from "@/hooks/useApi";
import { TeamLicenseInventoryDTO } from "@shaarp/common";
import { useCallback, useEffect, useState } from "react";

type UseTeamLicenseInventoryResult = {
  inventory: TeamLicenseInventoryDTO | undefined;
  isLoading: boolean;
  error: boolean;
  refetch: () => Promise<void>;
  /** Replaces the cached inventory (e.g. with the payload returned by a mutation). */
  setInventory: (inventory: TeamLicenseInventoryDTO | undefined) => void;
};

/**
 * Fetches the team license inventory on mount and exposes the loading/error
 * state plus a refetch handle, so pages don't each re-implement the same
 * fetch + retry plumbing.
 */
export const useTeamLicenseInventory = (): UseTeamLicenseInventoryResult => {
  const api = useApi();

  const [inventory, setInventory] = useState<
    TeamLicenseInventoryDTO | undefined
  >();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const { data } = await api.get<TeamLicenseInventoryDTO>(
        "/my/team/licenses"
      );
      setInventory(data);
    } catch {
      setInventory(undefined);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, []);

  return { inventory, isLoading, error, refetch, setInventory };
};
